import { message } from '@/common/components/Message';
import { COOKIE_ME_COMPANY } from '@/common/configs/auth';
import {
  ONBOARDING_PATHS,
  PUBLIC_PATHS,
  SUBSCRIPTION_PATHS,
  UNLEASH_TOGGLES,
} from '@/common/constants';
import { ParsedCookieMeCompany } from '@/common/types/auth';
import { parseErrorMessage } from '@/common/utils';
import Maintenance from '@/modules/auth/components/Maintenance';
import useMyUserCompanyQuery from '@/modules/company/hooks/queries/useMyUserCompanyQuery';
import { LoadingOutlined } from '@ant-design/icons';
import { UnleashClient, useVariant } from '@unleash/proxy-client-react';
import { Spin } from 'antd';
import { useSetAtom } from 'jotai';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { unleashAtom } from '../atoms';
import { UNLEASH_CONFIG } from '../configs';

const unleashClient = new UnleashClient({
  ...UNLEASH_CONFIG,
});

const FlagProvider = dynamic(() => import('@unleash/proxy-client-react'), {
  ssr: false,
  loading: () => (
    <div className="flex h-screen items-center justify-center">
      <Spin
        indicator={
          <LoadingOutlined style={{ fontSize: '64px', color: '#6014c3' }} />
        }
        spinning
      />
    </div>
  ),
});

const CustomUnleashContextProvider = ({ children }) => {
  const router = useRouter();
  const pathIsPublic = PUBLIC_PATHS.includes(router.pathname);
  const onboardingPath = ONBOARDING_PATHS.includes(router.pathname);
  const subscriptionPath = SUBSCRIPTION_PATHS.includes(router.pathname);
  const setLoadingFlag = useSetAtom(unleashAtom);

  const cookieMeCompany = Cookies.get(COOKIE_ME_COMPANY);
  const parsedCookieMeCompany = useMemo<
    ParsedCookieMeCompany | undefined
  >(() => {
    try {
      return cookieMeCompany ? JSON.parse(cookieMeCompany) : undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }, [cookieMeCompany]);

  const { data: userCompanyData, error: errorUserCompanyData } =
    useMyUserCompanyQuery({
      options: {
        enabled:
          !pathIsPublic &&
          !onboardingPath &&
          !subscriptionPath &&
          !parsedCookieMeCompany?.userCompanyEmailDomain,
        refetchOnWindowFocus: false,
        retry: false,
      },
    });

  /**
   *  Retrieve the value from cookie
   *  because there is a slow query issue on the me company endpoint
   */
  const userCompanyEmail =
    parsedCookieMeCompany?.userCompanyEmail ||
    userCompanyData?.data?.result?.email ||
    '';
  const userCompanyEmailDomain =
    parsedCookieMeCompany?.userCompanyEmailDomain ||
    userCompanyData?.data?.result?.company?.emailDomain ||
    '';

  useEffect(() => {
    if (errorUserCompanyData) {
      message.open({
        type: 'error',
        content: parseErrorMessage(errorUserCompanyData),
      });
      console.error(errorUserCompanyData);
    }
  }, [errorUserCompanyData]);

  useEffect(() => {
    const updateUnleashContext = async () => {
      setLoadingFlag({
        loading: true,
      });

      try {
        if (userCompanyEmail || userCompanyEmailDomain) {
          const properties = unleashClient?.getContext()?.properties || {};
          await unleashClient.updateContext({
            userId: userCompanyEmail,
            properties: {
              ...properties,
              companyEmailDomain: userCompanyEmailDomain,
            },
          });
        }
      } catch (error) {
        console.error(error);
        message.open({
          type: 'error',
          content: parseErrorMessage(error),
        });
      } finally {
        setLoadingFlag({
          loading: false,
        });
      }
    };

    updateUnleashContext();
  }, [userCompanyEmail, userCompanyEmailDomain]);

  return (
    <FlagProvider unleashClient={unleashClient}>
      <MaintenanceChecker>{children}</MaintenanceChecker>
    </FlagProvider>
  );
};

const MaintenanceChecker = ({ children }) => {
  const { pathname } = useRouter();
  const maintenanceVariant = useVariant(UNLEASH_TOGGLES.ed_maintenance);
  const parsedPayload = JSON.parse(maintenanceVariant.payload?.value ?? '{}');

  const maintenanceEnabled = maintenanceVariant.enabled;
  const maintenanceTarget = parsedPayload?.target as 'all' | 'specific';
  const maintenanceRoutes = parsedPayload?.routes as string[];

  if (
    maintenanceEnabled &&
    (maintenanceTarget === 'all' ||
      (maintenanceTarget === 'specific' &&
        maintenanceRoutes.includes(pathname)))
  ) {
    return <Maintenance />;
  }

  return children;
};

export default CustomUnleashContextProvider;
